import React, { useRef, useEffect, useState } from 'react';

interface AwarenessEditorBlockProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}

export const AwarenessEditorBlock: React.FC<AwarenessEditorBlockProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-expand height
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;

    // 1. Ctrl + B / b
    if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      const selectedText = text.substring(start, end);
      
      const hasAsterisksInside = selectedText.startsWith('*') && selectedText.endsWith('*') && selectedText.length >= 2;
      const textBeforeSelection = text.substring(0, start);
      const textAfterSelection = text.substring(end);
      const hasAsterisksOutside = textBeforeSelection.endsWith('*') && textAfterSelection.startsWith('*');

      let newValue = '';
      let newStart = start;
      let newEnd = end;

      if (hasAsterisksInside) {
        const unwrapped = selectedText.slice(1, -1);
        newValue = text.substring(0, start) + unwrapped + text.substring(end);
        newStart = start;
        newEnd = end - 2;
      } else if (hasAsterisksOutside) {
        newValue = text.substring(0, start - 1) + selectedText + text.substring(end + 1);
        newStart = start - 1;
        newEnd = end - 1;
      } else {
        const wrapped = `*${selectedText}*`;
        newValue = text.substring(0, start) + wrapped + text.substring(end);
        newStart = start + 1;
        newEnd = end + 1;
      }

      onChange(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newStart, newEnd);
      }, 0);
      return;
    }

    // 2. Enter key helpers
    if (e.key === 'Enter') {
      const upToCursor = text.substring(0, start);
      const lines = upToCursor.split('\n');
      const currentLine = lines[lines.length - 1];

      // Check FAQ pattern: Q1. ... ? or Q1: ... ? -> Enter -> A1: 
      const qMatch = currentLine.match(/^Q(\d+)(?:\.|\:)?\s*(.*)\?$/i);
      if (qMatch) {
        e.preventDefault();
        const num = qMatch[1];
        const inserted = `\nA${num}: `;
        const newValue = text.substring(0, start) + inserted + text.substring(end);
        onChange(newValue);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + inserted.length, start + inserted.length);
        }, 0);
        return;
      }

      // Check FAQ pattern: A1: ... or A1. ... -> Enter -> Q2.
      const aMatch = currentLine.match(/^A(\d+)(?:\.|\:)?\s*(.*)$/i);
      if (aMatch) {
        e.preventDefault();
        const nextNum = parseInt(aMatch[1], 10) + 1;
        const inserted = `\n\nQ${nextNum}. `;
        const newValue = text.substring(0, start) + inserted + text.substring(end);
        onChange(newValue);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + inserted.length, start + inserted.length);
        }, 0);
        return;
      }

      // Check Bullet pattern: starts with * , - , • 
      const bulletMatch = currentLine.match(/^([\*\-\u2022])\s+(.*)$/);
      if (bulletMatch) {
        e.preventDefault();
        const bulletChar = bulletMatch[1];
        const content = bulletMatch[2].trim();

        if (content === '') {
          // If the bullet line was empty, clear the bullet character (back out of list mode)
          const startOfLine = start - currentLine.length;
          const newValue = text.substring(0, startOfLine) + text.substring(end);
          onChange(newValue);
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(startOfLine, startOfLine);
          }, 0);
        } else {
          // Continue bullet list
          const inserted = `\n${bulletChar} `;
          const newValue = text.substring(0, start) + inserted + text.substring(end);
          onChange(newValue);
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + inserted.length, start + inserted.length);
          }, 0);
        }
        return;
      }
    }
  };

  return (
    <div className="space-y-2 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 bg-gray-50/50 dark:bg-gray-900/20 animate-fadeIn">
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 capitalize">
          {label}
        </label>
      </div>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          adjustHeight();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 outline-none transition-all resize-none overflow-hidden min-h-[120px]"
      />

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span>{"💡 Ctrl + B to bold"}</span>
        <span>{"• Auto FAQ: Q1... ? + Enter -> A1: -> Enter -> Q2."}</span>
        <span>{"• Bullets: Type \"* \" or \"- \" at line start"}</span>
      </div>
    </div>
  );
};
